// frontend/src/components/placements/PlacementForm.jsx
import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { getApiBaseURL } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";

const PlacementForm = ({ placement, onSave, onCancel }) => {
  const { user } = useContext(AuthContext);
  const apiBase = getApiBaseURL();

  const [formData, setFormData] = useState({
    consumer_id: "",
    employer_id: "",
    job_title: "",
    job_duties: "",
    hourly_wage: "",
    hours_per_week: "",
    employment_type: "full-time",
    placement_date: "",
    start_date: "",
    end_date: "",
    status: "pending",
    closure_reason: "",
    benefits_offered: "",
    placed_by: "",
  });

  const [consumers, setConsumers] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [jobCoaches, setJobCoaches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Load dropdowns
  useEffect(() => {
    const loadDropdowns = async () => {
      if (!user?.tenant_id) return;

      try {
        // Load consumers
        const consumersRes = await secureAxios.get(
          `${apiBase}/api/consumers/dropdown`,
          { params: { tenant_id: user.tenant_id } }
        );
        setConsumers(consumersRes.data);

        // Load employers (contacts)
        const employersRes = await secureAxios.get(
          `${apiBase}/api/employers/dropdown`,
          { params: { tenant_id: user.tenant_id } }
        );
        setEmployers(employersRes.data);

        // Load job coaches (users)
        const usersRes = await secureAxios.get(`${apiBase}/api/users`, {
          params: { tenant_id: user.tenant_id },
        });
        setJobCoaches(
          usersRes.data.filter(
            (u) =>
              u.role?.toLowerCase() === "job coach" ||
              u.role?.toLowerCase() === "admin"
          )
        );
      } catch (err) {
        console.error("Error loading dropdowns:", err);
      }
    };

    loadDropdowns();
  }, [user?.tenant_id, apiBase]);

  // Populate form when editing
  useEffect(() => {
    if (placement) {
      setFormData({
        consumer_id: placement.consumer_id || "",
        employer_id: placement.employer_id || "",
        job_title: placement.job_title || "",
        job_duties: placement.job_duties || "",
        hourly_wage: placement.hourly_wage || "",
        hours_per_week: placement.hours_per_week || "",
        employment_type: placement.employment_type || "full-time",
        placement_date: placement.placement_date
          ? placement.placement_date.split("T")[0]
          : "",
        start_date: placement.start_date
          ? placement.start_date.split("T")[0]
          : "",
        end_date: placement.end_date ? placement.end_date.split("T")[0] : "",
        status: placement.status || "pending",
        closure_reason: placement.closure_reason || "",
        benefits_offered: placement.benefits_offered || "",
        placed_by: placement.placed_by || "",
      });
    } else {
      // Default placed_by to current user
      setFormData((prev) => ({
        ...prev,
        placed_by: user?.user_id || "",
      }));
    }
  }, [placement, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (error) setError("");
  };

  const validateForm = () => {
    if (!formData.job_title.trim()) {
      setError("Job title is required");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setError("");

    const payload = {
      ...formData,
      tenant_id: user.tenant_id,
      hourly_wage: formData.hourly_wage
        ? parseFloat(formData.hourly_wage)
        : null,
      hours_per_week: formData.hours_per_week
        ? parseFloat(formData.hours_per_week)
        : null,
      placement_date: formData.placement_date || null,
      start_date: formData.start_date || null,
      end_date: formData.end_date || null,
      employer_id: formData.employer_id || null,
      placed_by: formData.placed_by || null,
      [placement ? "updated_by" : "created_by"]: user.user_id,
    };

    try {
      if (placement) {
        await secureAxios.put(
          `${apiBase}/api/placements/${placement.placement_id}`,
          payload
        );
        toast.success("Placement updated successfully");
      } else {
        await secureAxios.post(`${apiBase}/api/placements`, payload);
        toast.success("Placement created successfully");
      }

      onSave();
    } catch (err) {
      console.error("Error saving placement:", err);
      setError(err.response?.data?.error || "Failed to save placement");
    } finally {
      setLoading(false);
    }
  };

  const employmentTypes = [
    { value: "full-time", label: "Full-Time" },
    { value: "part-time", label: "Part-Time" },
    { value: "seasonal", label: "Seasonal" },
    { value: "temporary", label: "Temporary" },
  ];

  const statusOptions = [
    { value: "pending", label: "Pending" },
    { value: "active", label: "Active" },
    { value: "closed-successful", label: "Closed - Successful" },
    { value: "closed-unsuccessful", label: "Closed - Unsuccessful" },
  ];

  const showClosureReason =
    formData.status === "closed-successful" ||
    formData.status === "closed-unsuccessful";

  return (
    <div className="container my-4">
      {/* Header - Mobile Responsive */}
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center mb-3 gap-2">
        <h5 className="mb-0 h6 h5-md">
          <i className="bi bi-briefcase me-2"></i>
          {placement ? "Edit Placement" : "New Job Placement"}
        </h5>
        <button className="btn btn-outline-secondary btn-sm" onClick={onCancel}>
          <i className="bi bi-arrow-left me-1"></i>
          <span className="d-none d-sm-inline">Back to List</span>
          <span className="d-sm-none">Back</span>
        </button>
      </div>

      {error && (
        <div className="alert alert-danger alert-dismissible py-2">
          <small>{error}</small>
          <button
            type="button"
            className="btn-close"
            onClick={() => setError("")}
          ></button>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Consumer & Employer Card */}
        <div className="card">
          <div className="card-header bg-primary text-white py-2">
            <i className="bi bi-person me-2"></i>
            <span className="d-none d-sm-inline">Consumer & Employer</span>
            <span className="d-sm-none">Consumer/Employer</span>
          </div>
          <div className="card-body p-2 p-md-3">
            <div className="row g-2 g-md-3">
              <div className="col-12 col-md-6">
                <label className="form-label small">Consumer</label>
                <select
                  name="consumer_id"
                  className="form-select form-select-sm"
                  value={formData.consumer_id}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="">Select Consumer (Optional)</option>
                  {consumers.map((consumer) => (
                    <option
                      key={consumer.consumer_id}
                      value={consumer.consumer_id}
                    >
                      {consumer.name}
                    </option>
                  ))}
                </select>
                <small className="text-muted d-none d-sm-block">
                  Can be assigned later after placement is secured
                </small>
              </div>

              <div className="col-12 col-md-6">
                <label className="form-label small">Employer (Contact)</label>
                <select
                  name="employer_id"
                  className="form-select form-select-sm"
                  value={formData.employer_id}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="">Select Employer</option>
                  {employers.map((employer) => (
                    <option
                      key={employer.employer_id}
                      value={employer.employer_id}
                    >
                      {employer.company_name}
                    </option>
                  ))}
                </select>
                <small className="text-muted d-none d-md-block">
                  <i className="bi bi-file-earmark-text me-1"></i>
                  CRP-32: Employer Info (Name, Phone, Address, Contact)
                </small>
              </div>
            </div>
          </div>
        </div>

        {/* Job Details Card */}
        <div className="card mt-2 mt-md-3">
          <div className="card-header bg-success text-white py-2">
            <i className="bi bi-briefcase me-2"></i>
            Job Details
            <small className="ms-2 opacity-75 d-none d-sm-inline">(Syncs to CRP-32)</small>
          </div>
          <div className="card-body p-2 p-md-3">
            <div className="row g-2 g-md-3">
              <div className="col-12 col-md-6">
                <label className="form-label small">
                  Job Title <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  name="job_title"
                  className="form-control form-control-sm"
                  value={formData.job_title}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
                <small className="text-muted d-none d-md-block">
                  <i className="bi bi-file-earmark-text me-1"></i>
                  CRP-32: "Job Title"
                </small>
              </div>

              <div className="col-6 col-md-3">
                <label className="form-label small">Hourly Wage</label>
                <div className="input-group input-group-sm">
                  <span className="input-group-text">$</span>
                  <input
                    type="number"
                    name="hourly_wage"
                    className="form-control form-control-sm"
                    value={formData.hourly_wage}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    disabled={loading}
                  />
                </div>
                <small className="text-muted d-none d-md-block">
                  <i className="bi bi-file-earmark-text me-1"></i>
                  CRP-32: "Hourly Wage"
                </small>
              </div>

              <div className="col-6 col-md-3">
                <label className="form-label small">Hours/Week</label>
                <input
                  type="number"
                  name="hours_per_week"
                  className="form-control form-control-sm"
                  value={formData.hours_per_week}
                  onChange={handleChange}
                  step="0.5"
                  min="0"
                  max="168"
                  disabled={loading}
                />
                <small className="text-muted d-none d-md-block">
                  <i className="bi bi-file-earmark-text me-1"></i>
                  CRP-32: "Hours Worked Weekly"
                </small>
              </div>

              <div className="col-6 col-md-4">
                <label className="form-label small">Employment Type</label>
                <select
                  name="employment_type"
                  className="form-select form-select-sm"
                  value={formData.employment_type}
                  onChange={handleChange}
                  disabled={loading}
                >
                  {employmentTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-12 col-md-8">
                <label className="form-label small">Benefits Offered</label>
                <input
                  type="text"
                  name="benefits_offered"
                  className="form-control form-control-sm"
                  value={formData.benefits_offered}
                  onChange={handleChange}
                  placeholder="e.g., Health insurance, PTO, 401k"
                  disabled={loading}
                />
                <small className="text-muted d-none d-md-block">
                  <i className="bi bi-file-earmark-text me-1"></i>
                  CRP-32: "Benefits Received (Incentives)"
                </small>
              </div>

              <div className="col-12">
                <label className="form-label small">Job Duties</label>
                <textarea
                  name="job_duties"
                  className="form-control form-control-sm"
                  value={formData.job_duties}
                  onChange={handleChange}
                  rows="2"
                  placeholder="Describe the job duties and responsibilities..."
                  disabled={loading}
                />
                <small className="text-muted d-none d-md-block">
                  <i className="bi bi-file-earmark-text me-1"></i>
                  CRP-32: "Job Duties (See Attached)"
                </small>
              </div>
            </div>
          </div>
        </div>

        {/* Dates & Status Card */}
        <div className="card mt-2 mt-md-3">
          <div className="card-header bg-info text-white py-2">
            <i className="bi bi-calendar me-2"></i>
            Dates & Status
          </div>
          <div className="card-body p-2 p-md-3">
            <div className="row g-2 g-md-3">
              <div className="col-6 col-md-3">
                <label className="form-label small">Placement Date</label>
                <input
                  type="date"
                  name="placement_date"
                  className="form-control form-control-sm"
                  value={formData.placement_date}
                  onChange={handleChange}
                  disabled={loading}
                />
                <small className="text-muted d-none d-md-block">
                  <i className="bi bi-file-earmark-text me-1"></i>
                  CRP-32: "Date Placed"
                </small>
              </div>

              <div className="col-6 col-md-3">
                <label className="form-label small">Start Date</label>
                <input
                  type="date"
                  name="start_date"
                  className="form-control form-control-sm"
                  value={formData.start_date}
                  onChange={handleChange}
                  disabled={loading}
                />
                <small className="text-muted d-none d-md-block">
                  <i className="bi bi-file-earmark-text me-1"></i>
                  CRP-32: "Start Date"
                </small>
              </div>

              <div className="col-6 col-md-3">
                <label className="form-label small">End Date</label>
                <input
                  type="date"
                  name="end_date"
                  className="form-control form-control-sm"
                  value={formData.end_date}
                  onChange={handleChange}
                  disabled={loading}
                />
                <small className="text-muted d-none d-md-block">If employment ended</small>
              </div>

              <div className="col-6 col-md-3">
                <label className="form-label small">Status</label>
                <select
                  name="status"
                  className="form-select form-select-sm"
                  value={formData.status}
                  onChange={handleChange}
                  disabled={loading}
                >
                  {statusOptions.map((status) => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>

              {showClosureReason && (
                <div className="col-12">
                  <label className="form-label small">Closure Reason</label>
                  <textarea
                    name="closure_reason"
                    className="form-control form-control-sm"
                    value={formData.closure_reason}
                    onChange={handleChange}
                    rows="2"
                    placeholder="Explain why the placement ended..."
                    disabled={loading}
                  />
                </div>
              )}

              <div className="col-12 col-md-6">
                <label className="form-label small">Placed By</label>
                <select
                  name="placed_by"
                  className="form-select form-select-sm"
                  value={formData.placed_by}
                  onChange={handleChange}
                  disabled={loading}
                >
                  <option value="">Select Job Coach</option>
                  {jobCoaches.map((coach) => (
                    <option key={coach.user_id} value={coach.user_id}>
                      {coach.name}
                    </option>
                  ))}
                </select>
                <small className="text-muted d-none d-md-block">
                  Job coach who secured this placement
                </small>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Buttons - Mobile Responsive */}
        <div className="mt-3 d-flex flex-column flex-sm-row gap-2">
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Saving...
              </>
            ) : (
              <>
                <i className="bi bi-check-circle me-1"></i>
                {placement ? "Update" : "Create"}
                <span className="d-none d-sm-inline"> Placement</span>
              </>
            )}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default PlacementForm;